import React, { useEffect, useState } from "react";
import { Button, Container, Table, Spinner, Alert, Modal } from "react-bootstrap";
import { FaDownload, FaSchool } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import StateList from "../../helper/State_List.json";
import waterIcon from "../../assets/images/icon/neww/water.png";
import axiosConn from "../../config/axiosConn";
import HeaderAdmin from "../HeaderAdmin";


const RasActivityAdminPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalDistricts, setModalDistricts] = useState([]);
  const [modalState, setModalState] = useState("");

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosConn("get", "ras-activities");
        if (res.data && res.data.success) {
          const rawActivities = res.data.activities || [];
          // Filter out Kendriya Vidyalaya Sangathan / KVS
          const filtered = rawActivities.filter(act => {
            const state = (act.state || "").toUpperCase();
            const school = (act.school_name || "").toUpperCase();
            return !(
              state.includes("KENDRIYA VIDYALAYA") || 
              state === "KVS" ||
              school.includes("KENDRIYA VIDYALAYA") ||
              school.includes(" KVS") ||
              school.startsWith("KVS ")
            );
          });
          setActivities(filtered);
        } else {
          setError("Failed to fetch activities");
        }
      } catch (err) {
        setError(err.message || "Error fetching activities");
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, []);

  // Normalize state names to merge duplicates (handles case, spaces, aliases)
  const normalizeStateName = (name) => {
    if (!name) return '';
    // Remove ALL extra spaces (leading, trailing, internal duplicates), convert to uppercase
    const n = name.toUpperCase().trim().replace(/\s+/g, ' ');
    const stateAliases = {
      'TAMILNADU': 'TAMIL NADU',
      'TAMIL NADU': 'TAMIL NADU',
      'JAMMU AND KASHMIR': 'JAMMU & KASHMIR',
      'JAMMU & KASHMIR': 'JAMMU & KASHMIR',
      'ANDAMAN AND NICOBAR': 'ANDAMAN & NICOBAR ISLANDS',
      'ANDAMAN AND NICOBAR ISLANDS': 'ANDAMAN & NICOBAR ISLANDS',
      'ANDAMAN & NICOBAR ISLANDS': 'ANDAMAN & NICOBAR ISLANDS',
      'DADRA AND NAGAR HAVELI': 'DADRA & NAGAR HAVELI',
      'DADRA & NAGAR HAVELI': 'DADRA & NAGAR HAVELI',
      'DAMAN AND DIU': 'DAMAN & DIU',
      'DAMAN & DIU': 'DAMAN & DIU',
      'DELHI': 'DELHI',
      'NCT OF DELHI': 'DELHI',
      'NATIONAL CAPITAL TERRITORY OF DELHI': 'DELHI',
      'PONDICHERRY': 'PUDUCHERRY',
      'PUDUCHERRY': 'PUDUCHERRY',
    };
    // Return normalized name from aliases or the cleaned uppercase version
    return stateAliases[n] || n;
  };

  const getDistrictsForState = (state) => {
    const districtMap = {};
    const normalizedState = normalizeStateName(state);

    activities.forEach(act => {
      const actState = normalizeStateName(act.state);
      if (actState === normalizedState) {
        // Include activities even if district is missing - mark as UNKNOWN
        // Clean district name: remove extra spaces, convert to uppercase
        const districtRaw = act.district ? act.district.toUpperCase().trim().replace(/\s+/g, ' ') : 'UNKNOWN';
        const districtKey = districtRaw || 'UNKNOWN';

        const schoolId = act.udise_id || act.school_name || 'UNKNOWN';
        if (!districtMap[districtKey]) {
          districtMap[districtKey] = { schools: new Set(), docs: 0, ras1: 0, ras2: 0, ras3: 0 };
        }
        districtMap[districtKey].schools.add(schoolId);
        // Count the actual DOCUMENT (submission) once
        districtMap[districtKey].docs++;
        // Each column: count all activities of that type (regardless of completion status)
        const atype = (act.activity_type || '').toLowerCase();
        if (atype.includes('awareness')) districtMap[districtKey].ras1++;
        if (atype.includes('sitevisit')) districtMap[districtKey].ras2++;
        // For recharge: count only valid recharge pits (same as Table 9)
        if (atype.includes('recharge') && act.recharge && act.recharge.type) {
          const rechargeType = (act.recharge.type || '').toLowerCase().trim();
          // Match both short values and full labels (including Hindi text)
          if (rechargeType === 'flora' || rechargeType.includes('flora') || rechargeType.includes('native') || rechargeType.includes('देशी वनस्पति') ||
            rechargeType === 'pit' || rechargeType.includes('pit') || rechargeType.includes('पिट') ||
            rechargeType === 'well' || rechargeType.includes('well') || rechargeType.includes('dry') || rechargeType.includes('कुएं') ||
            rechargeType === 'trench' || rechargeType.includes('trench') || rechargeType.includes('ट्रेंच')) {
            districtMap[districtKey].ras3++;
          }
        }
      }
    });
    return Object.entries(districtMap)
      .map(([district, data]) => ({
        district,
        totalSchools: data.schools.size,
        totalDocs: data.docs,   // actual submission count = matches DB
        ras1: data.ras1,
        ras2: data.ras2,
        ras3: data.ras3,
      }))
      .sort((a, b) => b.totalDocs - a.totalDocs);
  };

  const handleViewDistricts = (state) => {
    setModalState(state);
    setModalDistricts(getDistrictsForState(state));
    setShowModal(true);
  };

  const downloadExcel = () => {
    if (activities.length === 0) {
      alert("No data available to download");
      return;
    }

    // --- DATA PROCESSING (Matching downloadPDF logic) ---
    const globalUniqueSchools = new Set();
    const schoolDataMap = new Map();

    activities.forEach(act => {
      const schoolId = act.udise_id ? act.udise_id.toString().trim() : (act.school_name || 'UNKNOWN');
      if (schoolId) {
        globalUniqueSchools.add(schoolId);
        if (!schoolDataMap.has(schoolId)) {
          schoolDataMap.set(schoolId, {
            state: normalizeStateName(act.state),
            locality: (act.locality || '').toLowerCase(),
            pm_shri: (act.pm_shri || '').toLowerCase(),
            school_type: act.school_type || 'Others'
          });
        }
      }
    });

    const stateStats = {};
    schoolDataMap.forEach((schoolData, schoolId) => {
      const state = schoolData.state;
      if (!state) return;
      if (!stateStats[state]) {
        stateStats[state] = {
          totalResponses: 0,
          ruralSchools: new Set(), semiUrbanSchools: new Set(), urbanSchools: new Set(), unknownLocalitySchools: new Set(),
          pmShriSchools: new Set(), otherSchools: new Set(),
          schoolTypeSchools: {},
          lectures: { experts: 0, students: 0, teachers: 0, count: 0 },
          seminars: { experts: 0, students: 0, teachers: 0, count: 0 },
          communityAwareness: 0,
          studentsSites: { bawdis: 0, lakes: 0, taals: 0, others: 0 },
          teachersSites: { bawdis: 0, lakes: 0, taals: 0, others: 0 },
          rechargePits: { nativeFlora: 0, waterPit: 0, dryWell: 0, trench: 0 }
        };
      }
      const stats = stateStats[state];
      const locality = schoolData.locality;
      if (locality === 'rural') stats.ruralSchools.add(schoolId);
      else if (locality === 'semi-urban' || locality === 'semi urban') stats.semiUrbanSchools.add(schoolId);
      else if (locality === 'urban') stats.urbanSchools.add(schoolId);
      else stats.unknownLocalitySchools.add(schoolId);

      if (schoolData.pm_shri === 'yes') stats.pmShriSchools.add(schoolId);
      else stats.otherSchools.add(schoolId);

      const schoolType = schoolData.school_type;
      if (!stats.schoolTypeSchools[schoolType]) stats.schoolTypeSchools[schoolType] = new Set();
      stats.schoolTypeSchools[schoolType].add(schoolId);
    });

    activities.forEach(act => {
      const state = normalizeStateName(act.state);
      if (!state || !stateStats[state]) return;
      const stats = stateStats[state];
      stats.totalResponses++;

      if (act.activity_type === 'awareness' && act.awareness) {
        if (act.awareness.lectureConducted === 'yes') {
          stats.lectures.count++;
          if (act.awareness.lectureConductedDetails) {
            stats.lectures.experts += act.awareness.lectureConductedDetails.experts || 0;
            stats.lectures.students += act.awareness.lectureConductedDetails.students || 0;
            stats.lectures.teachers += act.awareness.lectureConductedDetails.teachers || 0;
          }
        }
        if (act.awareness.seminarConducted === 'yes') {
          stats.seminars.count++;
          if (act.awareness.seminarConductedDetails) {
            stats.seminars.experts += act.awareness.seminarConductedDetails.experts || 0;
            stats.seminars.students += act.awareness.seminarConductedDetails.students || 0;
            stats.seminars.teachers += act.awareness.seminarConductedDetails.teachers || 0;
          }
        }
        if (act.awareness.communityConducted === 'yes') stats.communityAwareness++;
      }

      if (act.activity_type === 'sitevisit' && act.sitevisit) {
        const siteType = (act.sitevisit.siteType || '').toLowerCase().trim();
        let numStudents = Number(act.sitevisit.numStudents) || 0;
        let numTeachers = Number(act.sitevisit.numTeachers) || 0;
        if (numStudents > 10000) numStudents = 0;
        if (numTeachers > 500) numTeachers = 0;

        if (siteType === 'bawdis' || siteType.includes('bawdi') || siteType.includes('stepwell')) {
          stats.studentsSites.bawdis += numStudents;
          stats.teachersSites.bawdis += numTeachers;
        } else if (siteType === 'lakes' || siteType.includes('lake')) {
          stats.studentsSites.lakes += numStudents;
          stats.teachersSites.lakes += numTeachers;
        } else if (siteType === 'taals' || siteType.includes('taal') || siteType.includes('pond')) {
          stats.studentsSites.taals += numStudents;
          stats.teachersSites.taals += numTeachers;
        } else {
          stats.studentsSites.others += numStudents;
          stats.teachersSites.others += numTeachers;
        }
      }

      if ((act.activity_type || '').toLowerCase().includes('recharge') && act.recharge && act.recharge.type) {
        const rechargeType = (act.recharge.type || '').toLowerCase().trim();
        if (rechargeType === 'flora' || rechargeType.includes('flora') || rechargeType.includes('native') || rechargeType.includes('देशी वनस्पति')) stats.rechargePits.nativeFlora++;
        else if (rechargeType === 'pit' || rechargeType.includes('pit') || rechargeType.includes('पिट')) stats.rechargePits.waterPit++;
        else if (rechargeType === 'well' || rechargeType.includes('well') || rechargeType.includes('dry') || rechargeType.includes('कुएं')) stats.rechargePits.dryWell++;
        else if (rechargeType === 'trench' || rechargeType.includes('trench') || rechargeType.includes('ट्रेंच')) stats.rechargePits.trench++;
      }
    });

    const states = Object.keys(stateStats)
      .filter(s => s && s !== "KENDRIYA VIDYALAYA SANGHATHAN" && s !== "KVS")
      .sort();
    const expectedTotal = globalUniqueSchools.size;

    // --- WORKBOOK GENERATION ---
    const workbook = XLSX.utils.book_new();

    const createSheet = (tableTitle, headers, data, activityTitle = null) => {
      const aoaData = [];
      if (activityTitle) {
        aoaData.push([activityTitle]);
      }
      aoaData.push([tableTitle]);
      aoaData.push(headers);
      
      // Map data to headers
      data.forEach(row => {
        aoaData.push(headers.map(h => row[h]));
      });

      const ws = XLSX.utils.aoa_to_sheet(aoaData);
      
      const merges = [];
      let currentRow = 0;
      
      if (activityTitle) {
        merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: headers.length - 1 } });
        currentRow++;
      }
      
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: headers.length - 1 } });
      
      ws['!merges'] = merges;
      ws['!cols'] = headers.map((h, i) => ({ wch: i === 0 ? 35 : 20 }));
      
      return ws;
    };

    // Table 1: Total Number of responses from States/UTs
    const table1Headers = ["States/UTs/ABs", "Number of Responses"];
    const table1Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "Number of Responses": stateStats[state].totalResponses
    }));
    table1Rows.push({
      "States/UTs/ABs": "Grand Total",
      "Number of Responses": states.reduce((sum, state) => sum + stateStats[state].totalResponses, 0)
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 1: Total Number of responses from States/UTs/ABs", table1Headers, table1Rows), "1. Responses by States-UTs-ABs");

    // Table 2: School participation by locality
    const table2Headers = ["States/UTs/ABs", "Rural", "Semi-urban", "Urban", "Total"];
    const table2Rows = states.map(state => {
      const rural = stateStats[state].ruralSchools.size;
      const semiUrban = stateStats[state].semiUrbanSchools.size;
      const urban = stateStats[state].urbanSchools.size;
      const unknown = stateStats[state].unknownLocalitySchools.size;
      return {
        "States/UTs/ABs": state,
        "Rural": rural,
        "Semi-urban": semiUrban,
        "Urban": urban,
        "Total": rural + semiUrban + urban + unknown
      };
    });
    table2Rows.push({
      "States/UTs/ABs": "Grand Total",
      "Rural": states.reduce((sum, s) => sum + stateStats[s].ruralSchools.size, 0),
      "Semi-urban": states.reduce((sum, s) => sum + stateStats[s].semiUrbanSchools.size, 0),
      "Urban": states.reduce((sum, s) => sum + stateStats[s].urbanSchools.size, 0),
      "Total": expectedTotal
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 2: Total number of participating schools", table2Headers, table2Rows), "2. Participation Locality");

    // Table 3: PM SHRI Schools
    const table3Headers = ["States/UTs/ABs", "PM SHRI (Yes)", "Others", "Total"];
    const table3Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "PM SHRI (Yes)": stateStats[state].pmShriSchools.size,
      "Others": stateStats[state].otherSchools.size,
      "Total": stateStats[state].pmShriSchools.size + stateStats[state].otherSchools.size
    }));
    table3Rows.push({
      "States/UTs/ABs": "Grand Total",
      "PM SHRI (Yes)": states.reduce((sum, s) => sum + stateStats[s].pmShriSchools.size, 0),
      "Others": states.reduce((sum, s) => sum + stateStats[s].otherSchools.size, 0),
      "Total": expectedTotal
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 3: Total Number of participating PM SHRI schools and other schools", table3Headers, table3Rows), "3. PM SHRI Participation");

    // Table 4: Participation of type of schools
    const actualSchoolTypes = new Set();
    states.forEach(state => {
      Object.keys(stateStats[state].schoolTypeSchools).forEach(type => actualSchoolTypes.add(type));
    });
    const schoolTypesList = Array.from(actualSchoolTypes)
      .filter(type => type && type !== "8" && type !== "undefined" && type !== "null")
      .sort();

    const table4Headers = ["States/UTs/ABs", ...schoolTypesList];
    const table4Rows = states.map(state => {
      const row = { "States/UTs/ABs": state };
      schoolTypesList.forEach(type => {
        row[type] = stateStats[state].schoolTypeSchools[type] ? stateStats[state].schoolTypeSchools[type].size : 0;
      });
      return row;
    });
    const total4Row = { "States/UTs/ABs": "Grand Total" };
    schoolTypesList.forEach(type => {
      total4Row[type] = states.reduce((sum, s) => sum + (stateStats[s].schoolTypeSchools[type] ? stateStats[s].schoolTypeSchools[type].size : 0), 0);
    });
    table4Rows.push(total4Row);
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 4: Participation of type of schools", table4Headers, table4Rows), "4. School Types");

    // Activity 1: Awareness
    const activity1Title = "Activity 1 (Awareness Activities)";

    // Table 4: Lectures
    const table5Headers = ["States/UTs/ABs", "No. of Experts", "No. of Students", "No. of Teachers", "No. of Lectures"];
    const table5Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "No. of Experts": stateStats[state].lectures.experts,
      "No. of Students": stateStats[state].lectures.students,
      "No. of Teachers": stateStats[state].lectures.teachers,
      "No. of Lectures": stateStats[state].lectures.count
    }));
    table5Rows.push({
      "States/UTs/ABs": "Grand Total",
      "No. of Experts": states.reduce((sum, s) => sum + stateStats[s].lectures.experts, 0),
      "No. of Students": states.reduce((sum, s) => sum + stateStats[s].lectures.students, 0),
      "No. of Teachers": states.reduce((sum, s) => sum + stateStats[s].lectures.teachers, 0),
      "No. of Lectures": states.reduce((sum, s) => sum + stateStats[s].lectures.count, 0)
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 4: States/UTs/ABs-wise representation of participation in Lectures", table5Headers, table5Rows, activity1Title), "5. Lectures");

    // Table 5: Seminars
    const table6Headers = ["States/UTs/ABs", "No. of Experts", "No. of Students", "No. of Teachers", "No. of Seminars"];
    const table6Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "No. of Experts": stateStats[state].seminars.experts,
      "No. of Students": stateStats[state].seminars.students,
      "No. of Teachers": stateStats[state].seminars.teachers,
      "No. of Seminars": stateStats[state].seminars.count
    }));
    table6Rows.push({
      "States/UTs/ABs": "Grand Total",
      "No. of Experts": states.reduce((sum, s) => sum + stateStats[s].seminars.experts, 0),
      "No. of Students": states.reduce((sum, s) => sum + stateStats[s].seminars.students, 0),
      "No. of Teachers": states.reduce((sum, s) => sum + stateStats[s].seminars.teachers, 0),
      "No. of Seminars": states.reduce((sum, s) => sum + stateStats[s].seminars.count, 0)
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 5: States/UTs/ABs-wise representation of participation in Seminars", table6Headers, table6Rows, activity1Title), "6. Seminars");

    // Table 6: Community Awareness
    const table7Headers = ["States/UTs/ABs", "No. of Community Awareness Campaigns"];
    const table7Rows = states.map(state => {
      const awarenessCount = activities.filter(act => {
        const actState = normalizeStateName(act.state);
        const atype = (act.activity_type || '').toLowerCase();
        return actState === normalizeStateName(state) && atype.includes('awareness');
      }).length;
      return { "States/UTs/ABs": state, "No. of Community Awareness Campaigns": awarenessCount };
    });
    table7Rows.push({ "States/UTs/ABs": "Grand Total", "No. of Community Awareness Campaigns": activities.filter(act => (act.activity_type || '').toLowerCase().includes('awareness')).length });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 6: States/UTs/ABs-wise distribution of community awareness campaigns", table7Headers, table7Rows, activity1Title), "7. Community Awareness");

    // Activity 2: Site Visit
    const activity2Title = "Activity 2 (Water Conservation Site Visit)";

    // Table 7: Students Site Visit
    const table8Headers = ["States/UTs/ABs", "Bawdis (Stepwells)", "Lakes", "Taals (Ponds)", "Others"];
    const table8Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "Bawdis (Stepwells)": stateStats[state].studentsSites.bawdis,
      "Lakes": stateStats[state].studentsSites.lakes,
      "Taals (Ponds)": stateStats[state].studentsSites.taals,
      "Others": stateStats[state].studentsSites.others
    }));
    table8Rows.push({
      "States/UTs/ABs": "Grand Total",
      "Bawdis (Stepwells)": states.reduce((sum, s) => sum + stateStats[s].studentsSites.bawdis, 0),
      "Lakes": states.reduce((sum, s) => sum + stateStats[s].studentsSites.lakes, 0),
      "Taals (Ponds)": states.reduce((sum, s) => sum + stateStats[s].studentsSites.taals, 0),
      "Others": states.reduce((sum, s) => sum + stateStats[s].studentsSites.others, 0)
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 7: States/UTs/ABs-wise distribution of Students' Visits to local water conservation sites", table8Headers, table8Rows, activity2Title), "8. Students Site Visit");

    // Table 8: Teachers Site Visit
    const table9Headers = ["States/UTs/ABs", "Bawdis (Stepwells)", "Lakes", "Taals (Ponds)", "Others"];
    const table9Rows = states.map(state => ({
      "States/UTs/ABs": state,
      "Bawdis (Stepwells)": stateStats[state].teachersSites.bawdis,
      "Lakes": stateStats[state].teachersSites.lakes,
      "Taals (Ponds)": stateStats[state].teachersSites.taals,
      "Others": stateStats[state].teachersSites.others
    }));
    table9Rows.push({
      "States/UTs/ABs": "Grand Total",
      "Bawdis (Stepwells)": states.reduce((sum, s) => sum + stateStats[s].teachersSites.bawdis, 0),
      "Lakes": states.reduce((sum, s) => sum + stateStats[s].teachersSites.lakes, 0),
      "Taals (Ponds)": states.reduce((sum, s) => sum + stateStats[s].teachersSites.taals, 0),
      "Others": states.reduce((sum, s) => sum + stateStats[s].teachersSites.others, 0)
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 8: States/UTs/ABs-wise distribution of Teachers' Visits to local water conservation sites", table9Headers, table9Rows, activity2Title), "9. Teachers Site Visit");

    // Activity 3: Recharge Pits
    const activity3Title = "Activity 3 (Ground Water Recharge Pit)";

    // Table 9: Recharge Pits
    const table10Headers = ["States/UTs/ABs", "Native Flora Based", "Water Recharge Pit", "Dry Open Well", "Water Recharge Trench", "Total"];
    const table10Rows = states.map(state => {
      const s = stateStats[state].rechargePits;
      return {
        "States/UTs/ABs": state,
        "Native Flora Based": s.nativeFlora,
        "Water Recharge Pit": s.waterPit,
        "Dry Open Well": s.dryWell,
        "Water Recharge Trench": s.trench,
        "Total": s.nativeFlora + s.waterPit + s.dryWell + s.trench
      };
    });
    const total10 = {
      nf: states.reduce((sum, s) => sum + stateStats[s].rechargePits.nativeFlora, 0),
      wp: states.reduce((sum, s) => sum + stateStats[s].rechargePits.waterPit, 0),
      dw: states.reduce((sum, s) => sum + stateStats[s].rechargePits.dryWell, 0),
      rt: states.reduce((sum, s) => sum + stateStats[s].rechargePits.trench, 0)
    };
    table10Rows.push({
      "States/UTs/ABs": "Grand Total",
      "Native Flora Based": total10.nf,
      "Water Recharge Pit": total10.wp,
      "Dry Open Well": total10.dw,
      "Water Recharge Trench": total10.rt,
      "Total": total10.nf + total10.wp + total10.dw + total10.rt
    });
    XLSX.utils.book_append_sheet(workbook, createSheet("Table 9: States/UTs/ABs-wise distribution of Water Recharge Pits Constructed", table10Headers, table10Rows, activity3Title), "10. Recharge Pits");

    // Export XLSX
    XLSX.writeFile(workbook, `RAS_2025-26_Detailed_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const downloadPDF = () => {
    if (stateWiseData.length === 0) {
      alert("No data available to download");
      return;
    }

    // Create PDF using jsPDF
    const doc = new jsPDF();
    let yPos = 15;

    // Helper function to add page header
    const addPageHeader = (title) => {
      doc.setFontSize(16);
      doc.setTextColor(255, 102, 0);
      doc.text("RAS 2025-26 Report", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
      yPos += 10;
      if (title) {
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(title, doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
        yPos += 8;
      }
    };

    // Helper to check if new page is needed
    const checkNewPage = (requiredSpace = 40) => {
      if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPos = 15;
        return true;
      }
      return false;
    };

    // Create a global set of all unique schools for consistency
    const globalUniqueSchools = new Set();
    const schoolDataMap = new Map(); // Track first occurrence of each school's data

    activities.forEach(act => {
      const schoolId = act.udise_id ? act.udise_id.toString().trim() : (act.school_name || 'UNKNOWN');
      if (schoolId) {
        globalUniqueSchools.add(schoolId);
        // Store the first occurrence of each school's data to avoid inconsistencies
        if (!schoolDataMap.has(schoolId)) {
          schoolDataMap.set(schoolId, {
            state: normalizeStateName(act.state),
            locality: (act.locality || '').toLowerCase(),
            pm_shri: (act.pm_shri || '').toLowerCase(),
            school_type: act.school_type || 'Others'
          });
        }
      }
    });

    // Process data for all tables using consistent school data
    const stateStats = {};

    // Process each unique school only once
    schoolDataMap.forEach((schoolData, schoolId) => {
      const state = schoolData.state;
      if (!state) return;

      if (!stateStats[state]) {
        stateStats[state] = {
          totalResponses: 0,
          rural: 0, semiUrban: 0, urban: 0,
          // Track unique schools by locality for Table 2
          ruralSchools: new Set(), semiUrbanSchools: new Set(), urbanSchools: new Set(), unknownLocalitySchools: new Set(),
          pmShri: 0, others: 0,
          // Track unique schools by PM SHRI status for Table 3
          pmShriSchools: new Set(), otherSchools: new Set(),
          schoolTypes: {},
          // Track unique schools by school type for Table 4
          schoolTypeSchools: {},
          lectures: { experts: 0, students: 0, teachers: 0, count: 0 },
          seminars: { experts: 0, students: 0, teachers: 0, count: 0 },
          communityAwareness: 0,
          studentsSites: { bawdis: 0, lakes: 0, taals: 0, others: 0 },
          teachersSites: { bawdis: 0, lakes: 0, taals: 0, others: 0 },
          rechargePits: { nativeFlora: 0, waterPit: 0, dryWell: 0, trench: 0 }
        };
      }

      const stats = stateStats[state];

      // Locality - each school counted once per locality
      const locality = schoolData.locality;
      if (locality === 'rural') {
        stats.ruralSchools.add(schoolId);
      } else if (locality === 'semi-urban' || locality === 'semi urban') {
        stats.semiUrbanSchools.add(schoolId);
      } else if (locality === 'urban') {
        stats.urbanSchools.add(schoolId);
      } else {
        stats.unknownLocalitySchools.add(schoolId);
      }

      // PM SHRI - each school counted once per PM SHRI status
      if (schoolData.pm_shri === 'yes') {
        stats.pmShriSchools.add(schoolId);
      } else {
        stats.otherSchools.add(schoolId);
      }

      // School type - each school counted once per school type
      const schoolType = schoolData.school_type;
      if (!stats.schoolTypeSchools[schoolType]) {
        stats.schoolTypeSchools[schoolType] = new Set();
      }
      stats.schoolTypeSchools[schoolType].add(schoolId);
    });

    // Debug: Check all school types found in data
    const allSchoolTypes = new Set();
    schoolDataMap.forEach((schoolData) => {
      allSchoolTypes.add(schoolData.school_type);
    });
    console.log('All school types found in data:', Array.from(allSchoolTypes).sort());

    // Now process activities for submission counts and activity-specific data
    activities.forEach(act => {
      const state = normalizeStateName(act.state);
      if (!state || !stateStats[state]) return;

      const stats = stateStats[state];
      stats.totalResponses++;

      // Locality submission counts
      const locality = (act.locality || '').toLowerCase();
      if (locality === 'rural') stats.rural++;
      else if (locality === 'semi-urban' || locality === 'semi urban') stats.semiUrban++;
      else if (locality === 'urban') stats.urban++;

      // PM SHRI submission counts
      if ((act.pm_shri || '').toLowerCase() === 'yes') stats.pmShri++;
      else stats.others++;

      // School type submission counts
      const schoolType = act.school_type || 'Others';
      stats.schoolTypes[schoolType] = (stats.schoolTypes[schoolType] || 0) + 1;

      // Activity-specific data - count submissions, not individual events
      if (act.activity_type === 'awareness' && act.awareness) {
        // Count submissions that conducted lectures (not individual lecture events)
        if (act.awareness.lectureConducted === 'yes') {
          stats.lectures.count++;
          if (act.awareness.lectureConductedDetails) {
            stats.lectures.experts += act.awareness.lectureConductedDetails.experts || 0;
            stats.lectures.students += act.awareness.lectureConductedDetails.students || 0;
            stats.lectures.teachers += act.awareness.lectureConductedDetails.teachers || 0;
          }
        }
        // Count submissions that conducted seminars (not individual seminar events)
        if (act.awareness.seminarConducted === 'yes') {
          stats.seminars.count++;
          if (act.awareness.seminarConductedDetails) {
            stats.seminars.experts += act.awareness.seminarConductedDetails.experts || 0;
            stats.seminars.students += act.awareness.seminarConductedDetails.students || 0;
            stats.seminars.teachers += act.awareness.seminarConductedDetails.teachers || 0;
          }
        }
        // Count submissions that conducted community awareness (not individual campaigns)
        if (act.awareness.communityConducted === 'yes') {
          stats.communityAwareness++;
        }
      }

      if (act.activity_type === 'sitevisit' && act.sitevisit) {
        const siteType = (act.sitevisit.siteType || '').toLowerCase().trim();
        let numStudents = Number(act.sitevisit.numStudents) || 0;
        let numTeachers = Number(act.sitevisit.numTeachers) || 0;

        // Data cleaning: ignore extreme outliers (e.g. 80152 teachers/students which are data entry errors)
        if (numStudents > 10000) numStudents = 0;
        if (numTeachers > 500) numTeachers = 0;

        // Match exact form values: "bawdis", "lakes", "taals", or "other"
        if (siteType === 'bawdis' || siteType.includes('bawdi') || siteType.includes('stepwell')) {
          stats.studentsSites.bawdis += numStudents;
          stats.teachersSites.bawdis += numTeachers;
        } else if (siteType === 'lakes' || siteType.includes('lake')) {
          stats.studentsSites.lakes += numStudents;
          stats.teachersSites.lakes += numTeachers;
        } else if (siteType === 'taals' || siteType.includes('taal') || siteType.includes('pond')) {
          stats.studentsSites.taals += numStudents;
          stats.teachersSites.taals += numTeachers;
        } else {
          // Catch "other" and any custom values
          stats.studentsSites.others += numStudents;
          stats.teachersSites.others += numTeachers;
        }
      }

      if ((act.activity_type || '').toLowerCase().includes('recharge') && act.recharge && act.recharge.type) {
        const rechargeType = (act.recharge.type || '').toLowerCase().trim();
        // Match both short values and full labels (including Hindi text)
        if (rechargeType === 'flora' || rechargeType.includes('flora') || rechargeType.includes('native') || rechargeType.includes('देशी वनस्पति')) {
          stats.rechargePits.nativeFlora++;
        } else if (rechargeType === 'pit' || rechargeType.includes('pit') || rechargeType.includes('पिट')) {
          stats.rechargePits.waterPit++;
        } else if (rechargeType === 'well' || rechargeType.includes('well') || rechargeType.includes('dry') || rechargeType.includes('कुएं')) {
          stats.rechargePits.dryWell++;
        } else if (rechargeType === 'trench' || rechargeType.includes('trench') || rechargeType.includes('ट्रेंच')) {
          stats.rechargePits.trench++;
        }
        // Only count activities with valid types - ignore unrecognized types
      }
    });

    const states = Object.keys(stateStats)
      .filter(s => s && s !== "KENDRIYA VIDYALAYA SANGHATHAN" && s !== "KVS")
      .sort();

    // Validation: Ensure all tables count the same total unique schools
    const table2Total = states.reduce((sum, s) => {
      const ruralCount = stateStats[s].ruralSchools.size;
      const semiUrbanCount = stateStats[s].semiUrbanSchools.size;
      const urbanCount = stateStats[s].urbanSchools.size;
      const unknownCount = stateStats[s].unknownLocalitySchools.size;
      return sum + ruralCount + semiUrbanCount + urbanCount + unknownCount;
    }, 0);

    const table3Total = states.reduce((sum, s) => {
      return sum + stateStats[s].pmShriSchools.size + stateStats[s].otherSchools.size;
    }, 0);

    // Use the global count as the definitive total
    const expectedTotal = globalUniqueSchools.size;
    console.log(`School count validation - Global: ${expectedTotal}, Table2: ${table2Total}, Table3: ${table3Total}`);

    // Debug: Verify dashboard vs PDF counts
    const totalAwareness = activities.filter(act => (act.activity_type || '').toLowerCase().includes('awareness')).length;
    const totalSiteVisit = activities.filter(act => (act.activity_type || '').toLowerCase().includes('sitevisit')).length;
    const totalRecharge = activities.filter(act => (act.activity_type || '').toLowerCase().includes('recharge')).length;

    console.log('=== DASHBOARD VS PDF VERIFICATION ===');
    console.log(`Total Awareness Submissions: ${totalAwareness}`);
    console.log(`Total Site Visit Submissions: ${totalSiteVisit}`);
    console.log(`Total Recharge Submissions: ${totalRecharge}`);
    console.log('=====================================');

    // Table 1: Total Number of responses from States/UTs
    addPageHeader("Table 1: Total Number of responses from States/UTs/ABs");
    const table1Data = states.map(state => [state, stateStats[state].totalResponses]);
    const grandTotal1 = states.reduce((sum, state) => sum + stateStats[state].totalResponses, 0);
    table1Data.push(["Grand Total", grandTotal1]);

    doc.autoTable({
      head: [["States/UTs/ABs", "Number of Responses"]],
      body: table1Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center' },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table1Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Table 2: School participation by locality - use unique school counts
    checkNewPage(60);
    doc.setFontSize(12);
    doc.text("Table 2: Total number of participating schools", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    doc.setFontSize(8);
    // doc.text(`(Expected Total: ${expectedTotal} unique schools)`, doc.internal.pageSize.getWidth() / 2, yPos + 5, { align: "center" });
    yPos += 12;

    const table2Data = states.map(state => {
      const ruralCount = stateStats[state].ruralSchools.size;
      const semiUrbanCount = stateStats[state].semiUrbanSchools.size;
      const urbanCount = stateStats[state].urbanSchools.size;
      const unknownCount = stateStats[state].unknownLocalitySchools.size;
      return [
        state,
        ruralCount,
        semiUrbanCount,
        urbanCount,
        ruralCount + semiUrbanCount + urbanCount + unknownCount
      ];
    });
    const total2 = {
      rural: states.reduce((sum, s) => sum + stateStats[s].ruralSchools.size, 0),
      semiUrban: states.reduce((sum, s) => sum + stateStats[s].semiUrbanSchools.size, 0),
      urban: states.reduce((sum, s) => sum + stateStats[s].urbanSchools.size, 0),
      unknown: states.reduce((sum, s) => sum + stateStats[s].unknownLocalitySchools.size, 0)
    };
    // Force the total to match the global count
    total2.total = expectedTotal;
    table2Data.push(["Grand Total", total2.rural, total2.semiUrban, total2.urban, total2.total]);

    doc.autoTable({
      head: [["States/UTs/ABs", "Rural", "Semi-urban", "Urban", "Total"]],
      body: table2Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table2Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Table 3: PM SHRI Schools - use unique school counts
    checkNewPage(60);
    doc.setFontSize(12);
    doc.text("Table 3: Total Number of participating PM SHRI schools and other schools", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    doc.setFontSize(8);
    // doc.text(`(Expected Total: ${expectedTotal} unique schools)`, doc.internal.pageSize.getWidth() / 2, yPos + 5, { align: "center" });
    yPos += 12;

    const table3Data = states.map(state => [
      state,
      stateStats[state].pmShriSchools.size,
      stateStats[state].otherSchools.size,
      stateStats[state].pmShriSchools.size + stateStats[state].otherSchools.size
    ]);
    const total3 = {
      pmShri: states.reduce((sum, s) => sum + stateStats[s].pmShriSchools.size, 0),
      others: states.reduce((sum, s) => sum + stateStats[s].otherSchools.size, 0)
    };
    // Force the total to match the global count
    total3.total = expectedTotal;
    table3Data.push(["Grand Total", total3.pmShri, total3.others, total3.total]);

    doc.autoTable({
      head: [["States/UTs/ABs", "PM SHRI (Yes)", "Others", "Total"]],
      body: table3Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table3Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Table 4: Type of schools - use unique school counts with actual school types from data
    doc.addPage();
    yPos = 15;
    doc.setFontSize(12);
    doc.text("Table 4: Participation of type of schools", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    doc.setFontSize(8);
    // doc.text(`(Expected Total: ${expectedTotal} unique schools)`, doc.internal.pageSize.getWidth() / 2, yPos + 5, { align: "center" });
    yPos += 12;

    // Get all actual school types from the data and sort them
    const actualSchoolTypes = new Set();
    states.forEach(state => {
      Object.keys(stateStats[state].schoolTypeSchools).forEach(type => {
        actualSchoolTypes.add(type);
      });
    });
    const schoolTypesList = Array.from(actualSchoolTypes)
      .filter(type => type && type !== "8" && type !== "undefined" && type !== "null")
      .sort();

    console.log('Using actual school types from data:', schoolTypesList);

    const table4Data = states.map(state => {
      const row = [state];

      schoolTypesList.forEach(type => {
        const uniqueSchools = stateStats[state].schoolTypeSchools[type] ? stateStats[state].schoolTypeSchools[type].size : 0;
        row.push(uniqueSchools);
      });

      return row;
    });

    const total4 = new Array(schoolTypesList.length).fill(0);
    states.forEach(state => {
      schoolTypesList.forEach((type, idx) => {
        const uniqueSchools = stateStats[state].schoolTypeSchools[type] ? stateStats[state].schoolTypeSchools[type].size : 0;
        total4[idx] += uniqueSchools;
      });
    });

    // Verify the total matches
    const calculatedTotal4 = total4.reduce((sum, count) => sum + count, 0);
    console.log(`Table 4 validation - Calculated: ${calculatedTotal4}, Expected: ${expectedTotal}`);

    table4Data.push(["Grand Total", ...total4]);

    doc.autoTable({
      head: [["States/UTs/ABs", ...schoolTypesList]],
      body: table4Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 5 },
      styles: { fontSize: 5, cellPadding: 1 },
      columnStyles: Object.fromEntries([...Array(schoolTypesList.length + 1).keys()].map(i => [i, { halign: 'center' }])),
      didParseCell: function (data) {
        if (data.row.index === table4Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Table 5: Lectures - should match dashboard awareness campaign count
    doc.addPage();
    yPos = 15;
    doc.setFontSize(12);
    doc.text("Activity 1 (Awareness Campaign)", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;
    doc.setFontSize(10);
    doc.text("Table 4: States/UTs/ABs-wise representation of participation in Lectures", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    // Use the same logic as dashboard - count awareness submissions per state
    const table5Data = states.map(state => {
      return [
        state,
        stateStats[state].lectures.experts,
        stateStats[state].lectures.students,
        stateStats[state].lectures.teachers,
        stateStats[state].lectures.count // Use actual lecture count
      ];
    });

    const total5 = {
      experts: states.reduce((sum, s) => sum + stateStats[s].lectures.experts, 0),
      students: states.reduce((sum, s) => sum + stateStats[s].lectures.students, 0),
      teachers: states.reduce((sum, s) => sum + stateStats[s].lectures.teachers, 0),
      count: states.reduce((sum, s) => sum + stateStats[s].lectures.count, 0) // Use actual lecture count
    };
    table5Data.push(["Grand Total", total5.experts, total5.students, total5.teachers, total5.count]);

    doc.autoTable({
      head: [["States/UTs/ABs", "No. of Experts", "No. of Students", "No. of Teachers", "No. of Lectures"]],
      body: table5Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table5Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Table 6: Seminars - should match dashboard awareness campaign count
    checkNewPage(60);
    doc.setFontSize(10);
    doc.text("Table 5: States/UTs/ABs-wise representation of participation in Seminars", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    const table6Data = states.map(state => {
      return [
        state,
        stateStats[state].seminars.experts,
        stateStats[state].seminars.students,
        stateStats[state].seminars.teachers,
        stateStats[state].seminars.count // Use actual seminar count
      ];
    });

    const total6 = {
      experts: states.reduce((sum, s) => sum + stateStats[s].seminars.experts, 0),
      students: states.reduce((sum, s) => sum + stateStats[s].seminars.students, 0),
      teachers: states.reduce((sum, s) => sum + stateStats[s].seminars.teachers, 0),
      count: states.reduce((sum, s) => sum + stateStats[s].seminars.count, 0) // Use actual seminar count
    };
    table6Data.push(["Grand Total", total6.experts, total6.students, total6.teachers, total6.count]);

    doc.autoTable({
      head: [["States/UTs/ABs", "No. of Experts", "No. of Students", "No. of Teachers", "No. of Seminars"]],
      body: table6Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table6Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Table 7: Community Awareness - count actual community awareness campaigns conducted
    checkNewPage(60);
    doc.setFontSize(10);
    doc.text("Table 6: States/UTs/ABs-wise distribution of community awareness campaigns", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    const table7Data = states.map(state => {
      // Count awareness submissions for this state (same as dashboard ras1)
      const awarenessSubmissions = activities.filter(act => {
        const actState = (act.state || '').toUpperCase().trim();
        const normalizedActState = normalizeStateName(actState);
        const normalizedState = normalizeStateName(state);
        const atype = (act.activity_type || '').toLowerCase();
        return normalizedActState === normalizedState && atype.includes('awareness');
      }).length;

      return [state, awarenessSubmissions];
    });

    const total7 = activities.filter(act => {
      const atype = (act.activity_type || '').toLowerCase();
      return atype.includes('awareness');
    }).length;
    table7Data.push(["Grand Total", total7]);

    doc.autoTable({
      head: [["States/UTs/ABs", "No. of Community Awareness Campaigns"]],
      body: table7Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table7Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Table 8: Students visiting sites - should match dashboard site visit submission count
    doc.addPage();
    yPos = 15;
    doc.setFontSize(12);
    doc.text("Activity 2 (Water Conservation Site Visit)", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;
    doc.setFontSize(10);
    doc.text("Table 7: States/UTs/ABs-wise distribution of Students' Visits to local water conservation sites", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    const table8Data = states.map(state => {
      return [
        state,
        stateStats[state].studentsSites.bawdis,
        stateStats[state].studentsSites.lakes,
        stateStats[state].studentsSites.taals,
        stateStats[state].studentsSites.others
      ];
    });
    const total8 = {
      bawdis: states.reduce((sum, s) => sum + stateStats[s].studentsSites.bawdis, 0),
      lakes: states.reduce((sum, s) => sum + stateStats[s].studentsSites.lakes, 0),
      taals: states.reduce((sum, s) => sum + stateStats[s].studentsSites.taals, 0),
      others: states.reduce((sum, s) => sum + stateStats[s].studentsSites.others, 0)
    };
    table8Data.push(["Grand Total", total8.bawdis, total8.lakes, total8.taals, total8.others]);

    doc.autoTable({
      head: [["States/UTs/ABs", "Bawdis (Stepwells)", "Lakes", "Taals (Ponds)", "Others"]],
      body: table8Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table8Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Table 9: Teachers visiting sites - should match dashboard site visit submission count
    checkNewPage(60);
    doc.setFontSize(10);
    doc.text("Table 8: States/UTs/ABs-wise distribution of Teachers' Visits to local water conservation sites", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    const table9Data = states.map(state => {
      return [
        state,
        stateStats[state].teachersSites.bawdis,
        stateStats[state].teachersSites.lakes,
        stateStats[state].teachersSites.taals,
        stateStats[state].teachersSites.others
      ];
    });
    const total9 = {
      bawdis: states.reduce((sum, s) => sum + stateStats[s].teachersSites.bawdis, 0),
      lakes: states.reduce((sum, s) => sum + stateStats[s].teachersSites.lakes, 0),
      taals: states.reduce((sum, s) => sum + stateStats[s].teachersSites.taals, 0),
      others: states.reduce((sum, s) => sum + stateStats[s].teachersSites.others, 0)
    };
    table9Data.push(["Grand Total", total9.bawdis, total9.lakes, total9.taals, total9.others]);

    doc.autoTable({
      head: [["States/UTs/ABs", "Bawdis (Stepwells)", "Lakes", "Taals (Ponds)", "Others"]],
      body: table9Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 7 },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table9Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Table 10: Recharge pits - count actual recharge pits constructed
    doc.addPage();
    yPos = 15;
    doc.setFontSize(12);
    doc.text("Activity 3 (Ground Water Recharge Pit)", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;
    doc.setFontSize(10);
    doc.text("Table 9: States/UTs/ABs-wise distribution of Water Recharge Pits Constructed", doc.internal.pageSize.getWidth() / 2, yPos, { align: "center" });
    yPos += 8;

    const table10Data = states.map(state => {
      const s = stateStats[state].rechargePits;
      // Calculate total pits constructed (sum of all pit types, excluding unspecified)
      const totalPitsConstructed = s.nativeFlora + s.waterPit + s.dryWell + s.trench;
      return [state, s.nativeFlora, s.waterPit, s.dryWell, s.trench, totalPitsConstructed];
    });

    // Calculate totals directly from all activities (not just state aggregation)
    const totals = {
      nativeFlora: 0,
      waterPit: 0,
      dryWell: 0,
      trench: 0
    };

    // Calculate totals from all activities directly
    activities.forEach(act => {
      if ((act.activity_type || '').toLowerCase().includes('recharge') && act.recharge && act.recharge.type) {
        const rechargeType = (act.recharge.type || '').toLowerCase().trim();
        // Match both short values and full labels (including Hindi text)
        if (rechargeType === 'flora' || rechargeType.includes('flora') || rechargeType.includes('native') || rechargeType.includes('देशी वनस्पति')) {
          totals.nativeFlora++;
        } else if (rechargeType === 'pit' || rechargeType.includes('pit') || rechargeType.includes('पिट')) {
          totals.waterPit++;
        } else if (rechargeType === 'well' || rechargeType.includes('well') || rechargeType.includes('dry') || rechargeType.includes('कुएं')) {
          totals.dryWell++;
        } else if (rechargeType === 'trench' || rechargeType.includes('trench') || rechargeType.includes('ट्रेंच')) {
          totals.trench++;
        }
      }
    });

    const grandTotal = totals.nativeFlora + totals.waterPit + totals.dryWell + totals.trench;
    table10Data.push(["Grand Total", totals.nativeFlora, totals.waterPit, totals.dryWell, totals.trench, grandTotal]);

    doc.autoTable({
      head: [["States/UTs/ABs", "Native Flora Based", "Water Recharge Pit", "Dry Open Well", "Water Recharge Trench", "Total"]],
      body: table10Data,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [76, 175, 80], halign: 'center', fontSize: 6 },
      styles: { fontSize: 6, cellPadding: 1 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
      didParseCell: function (data) {
        if (data.row.index === table10Data.length - 1) {
          data.cell.styles.fillColor = [232, 245, 233];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(102, 102, 102);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    // Download the PDF
    doc.save(`RAS_2025-26_Detailed_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const downloadRasAllSchoolsExcel = () => {
    if (activities.length === 0) {
      alert("No data available to download");
      return;
    }

    const getStateName = (stateId) => {
      if (!stateId) return "";
      const state = StateList.find((s) => s.state_id === stateId);
      let stateName = state ? state.state_name : stateId;
      
      // Normalize Tamilnadu to Tamil Nadu
      if (stateName && stateName.toUpperCase().replace(/\s/g, '') === "TAMILNADU") {
        return "Tamil Nadu";
      }
      return stateName;
    };

    // Get unique schools based on UDISE Code
    const uniqueSchoolsMap = new Map();
    activities.forEach((act) => {
      const udise = act.udise_id ? act.udise_id.toString().trim() : "N/A";
      if (!uniqueSchoolsMap.has(udise)) {
        uniqueSchoolsMap.set(udise, {
          "Name of School": act.school_name || "N/A",
          "UDISE Code": udise,
          "School Type": act.school_type || "N/A",
          "PM Shri": act.pm_shri === "yes" ? "Yes" : act.pm_shri === "no" ? "No" : act.pm_shri || "N/A",
          "State/UT/AB": (getStateName(act.state) || act.state || "N/A").toUpperCase().trim(),
          "Locality (Urban/Rural)": act.locality ? (act.locality.charAt(0).toUpperCase() + act.locality.slice(1)) : "N/A"
        });
      }
    });

    const sortedSchools = Array.from(uniqueSchoolsMap.values()).sort((a, b) => {
      const stateResult = a["State/UT/AB"].localeCompare(b["State/UT/AB"], undefined, { sensitivity: 'base' });
      if (stateResult !== 0) return stateResult;
      return a["Name of School"].localeCompare(b["Name of School"], undefined, { sensitivity: 'base' });
    });

    // Create Excel Workbook
    const worksheet = XLSX.utils.json_to_sheet(sortedSchools);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Participated Schools");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 45 }, { wch: 15 }, { wch: 25 }, { wch: 10 }, { wch: 25 }, { wch: 20 }
    ];

    XLSX.writeFile(workbook, `RAS_Participated_Schools_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const activitiesCount = activities.length;
  const schoolsSet = new Set(activities.filter(a => a.udise_id).map(a => a.udise_id.toString().trim()));
  const schoolsCount = schoolsSet.size;

  const stateWiseData = [];
  const stateMap = {};
  activities.forEach(act => {
    // Clean state name: remove extra spaces, convert to uppercase, then normalize
    const rawState = act.state ? act.state.trim().replace(/\s+/g, ' ') : '';
    if (!rawState) return;
    // Normalize to merge duplicate state name variants (e.g. TAMILNADU → TAMIL NADU)
    const key = normalizeStateName(rawState);

    if (!stateMap[key]) {
      stateMap[key] = {
        name: key,
        docs: 0,       // actual submissions count = matches DB document count
        ras1: 0,       // awareness submissions (can overlap with ras2/ras3)
        ras2: 0,       // sitevisit submissions
        ras3: 0,       // recharge submissions
        schools: new Set(),
      };
    }
    // Count this document once for total submissions
    stateMap[key].docs++;
    // Each activity column: count all activities of that type (regardless of completion status)
    const atype = (act.activity_type || '').toLowerCase();
    if (atype.includes('awareness')) {
      stateMap[key].ras1++;
    }
    if (atype.includes('sitevisit')) {
      stateMap[key].ras2++;
    }
    // For recharge: count only valid recharge pits (same as Table 9)
    if (atype.includes('recharge') && act.recharge && act.recharge.type) {
      const rechargeType = (act.recharge.type || '').toLowerCase().trim();
      // Match both short values and full labels (including Hindi text)
      if (rechargeType === 'flora' || rechargeType.includes('flora') || rechargeType.includes('native') || rechargeType.includes('देशी वनस्पति') ||
        rechargeType === 'pit' || rechargeType.includes('pit') || rechargeType.includes('पिट') ||
        rechargeType === 'well' || rechargeType.includes('well') || rechargeType.includes('dry') || rechargeType.includes('कुएं') ||
        rechargeType === 'trench' || rechargeType.includes('trench') || rechargeType.includes('ट्रेंच')) {
        stateMap[key].ras3++;
      }
    }
    stateMap[key].schools.add(act.udise_id ? act.udise_id.toString().trim() : (act.school_name || 'UNKNOWN'));
  });
  for (const k in stateMap) {
    if (k !== "KENDRIYA VIDYALAYA SANGHATHAN" && k !== "KVS") {
      stateWiseData.push(stateMap[k]);
    }
  }
  // Sort by actual document count (submissions) not inflated activity sum
  stateWiseData.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <HeaderAdmin />
      <style>{`
        @media (max-width: 768px) {
          .dashbrdTopSrchDiv h1 {
            font-size: 1.5rem !important;
          }
          .dashbrdTopSrchDiv h2 {
            font-size: 1.2rem !important;
          }
          .dashbrdTopSrchDiv h3 {
            font-size: 1rem !important;
          }
          .dashbrdTopSrchDiv .text-end {
            text-align: center !important;
          }
          .dashbrdTopSrchDiv .text-end button {
            width: 100%;
            margin-bottom: 10px !important;
          }
          .ras-stats-container {
            flex-direction: column !important;
          }
          .boxdiv {
            width: 100% !important;
            margin-bottom: 15px !important;
          }
          .table-responsive {
            font-size: 0.85rem;
          }
          .table-responsive th,
          .table-responsive td {
            padding: 8px 4px !important;
            white-space: nowrap;
          }
        }
        @media (max-width: 576px) {
          .dashbrdTopSrchDiv h1 {
            font-size: 1.2rem !important;
          }
          .dashbrdTopSrchDiv h2 {
            font-size: 1rem !important;
          }
          .dashbrdTopSrchDiv h3 {
            font-size: 0.9rem !important;
          }
          .table-responsive {
            font-size: 0.75rem;
          }
        }
      `}</style>
      <section className="scholdashbrdSec superAdminDashboardSec">
        <Container fluid>
          <div className="dashbrdTopSrchDiv lavel mt-3 mb-1" style={{ position: "relative", zIndex: 999 }}>
            <h1 style={{ color: "orange" }}>RAS ACTIVITY ADMIN</h1>
            <h2>NATIONAL<span className="hdngBrkr"></span> SNAPSHOT</h2>
            <h3 style={{ letterSpacing: "0.1em" }}>Welcome RAS Activity Admin</h3>
            <div className="text-end">
              <Button className="me-2" variant="primary" onClick={downloadRasAllSchoolsExcel}><FaDownload size={20} color="#dddddd" /> Download RAS Schools Report</Button>
              <Button className="me-2" onClick={downloadPDF}><FaDownload size={20} color="#dddddd" /> Download PDF</Button>
              <Button variant="success" onClick={downloadExcel}><FaDownload size={20} color="#dddddd" /> Download Excel</Button>
            </div>
          </div>

          <div className="dashbrdTopSrchDivlavel mt-2 mb-0" style={{ position: "relative", zIndex: 0 }}>
            <div className="d-flex justify-content-center" style={{ flexWrap: "wrap", gap: "15px" }}>
              <div className="boxdiv boxdivOne">
                <div className="d-flex justify-content-between p-3">
                  <div>
                    <p> RAS <span className="text-success w100">ACTIVITIES</span></p>
                  </div>
                  <img src={waterIcon} alt="Water Icon" style={{ width: 82, height: 82 }} />
                </div>
                <div className="d-flex justify-content-center bg-success bottomBox">
                  <div className="bgdanger w-100">
                    <p>TOTAL</p>
                    <h2>{activitiesCount}</h2>
                  </div>
                </div>
              </div>
              <div className="boxdiv boxdivTwo">
                <div className="d-flex justify-content-between p-3">
                  <div>
                    <p>NUMBER OF <span className="text-success w100">SCHOOLS </span></p>
                  </div>
                  <FaSchool size={82} color="#9c6829" />
                </div>
                <div className="d-flex justify-content-between bg-success bottomBox">
                  <div className="bgdanger w-100">
                    <p>TOTAL</p>
                    <h2>{schoolsCount}</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>

        <Container fluid>
          <div className="container-wrapper mt-4">
            <h4 className="mb-3">RAS Activities (States/UTs/ABs Wise)</h4>
            {loading ? (
              <div className="text-center my-4"><Spinner animation="border" variant="primary" /></div>
            ) : error ? (
              <Alert variant="danger">{error}</Alert>
            ) : (
              <div className="table-responsive">
                <Table hover striped className="w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>States/UTs/ABs</th>
                      <th>Total<br />Submissions</th>
                      <th>Awareness<br />(RAS 1)</th>
                      <th>Site Visit<br />(RAS 2)</th>
                      <th>Recharge Pits<br />(RAS 3)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stateWiseData.map((item, index) => (
                      <tr key={item.name + index}>
                        <td>{index + 1}</td>
                        <td>{item.name || '-'}</td>
                        <td><b>{item.docs}</b></td>
                        <td>{item.ras1}</td>
                        <td>{item.ras2}</td>
                        <td>{item.ras3}</td>
                        <td>
                          <Button className="btn btn-primary btn-sm" onClick={() => handleViewDistricts(item.name)}>View Districts</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            <Modal show={showModal} onHide={() => setShowModal(false)} fullscreen="md-down" size="xl">
              <Modal.Header closeButton style={{ background: '#d4edda', fontSize: 'clamp(1rem, 2vw, 2rem)' }}>
                <Modal.Title style={{ fontWeight: 700, fontSize: 'clamp(1.2rem, 2.5vw, 2.2rem)' }}>Districts in {modalState}</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: 'clamp(1rem, 2vw, 2rem)' }}>
                {modalDistricts.length === 0 ? (
                  <p style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>No districts found.</p>
                ) : (
                  <>
                    <div className="d-flex gap-3 mb-3 flex-wrap">
                      <span className="badge bg-primary fs-6">Total Districts: {modalDistricts.length}</span>
                      <span className="badge bg-success fs-6">Total Schools: {modalDistricts.reduce((s, d) => s + d.totalSchools, 0)}</span>
                      <span className="badge bg-warning text-dark fs-6">Total Submissions: {modalDistricts.reduce((s, d) => s + d.totalDocs, 0)}</span>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover" style={{ minWidth: '500px', fontSize: 'clamp(0.85rem, 1.5vw, 1.2rem)' }}>
                        <thead style={{ background: '#d4edda' }}>
                          <tr>
                            <th>#</th>
                            <th>DISTRICT</th>
                            <th>SCHOOLS</th>
                            <th>TOTAL<br />SUBMISSIONS</th>
                            <th>AWARENESS<br />(RAS 1)</th>
                            <th>SITE VISIT<br />(RAS 2)</th>
                            <th>RECHARGE PITS<br />(RAS 3)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalDistricts.map((d, idx) => (
                            <tr key={d.district + idx}>
                              <td>{idx + 1}</td>
                              <td><b>{d.district}</b></td>
                              <td>{d.totalSchools}</td>
                              <td><b>{d.totalDocs}</b></td>
                              <td>{d.ras1}</td>
                              <td>{d.ras2}</td>
                              <td>{d.ras3}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot style={{ background: '#f8f9fa', fontWeight: 700 }}>
                          <tr>
                            <td colSpan={2}>TOTAL</td>
                            <td>{modalDistricts.reduce((s, d) => s + d.totalSchools, 0)}</td>
                            <td>{modalDistricts.reduce((s, d) => s + d.totalDocs, 0)}</td>
                            <td>{modalDistricts.reduce((s, d) => s + d.ras1, 0)}</td>
                            <td>{modalDistricts.reduce((s, d) => s + d.ras2, 0)}</td>
                            <td>{modalDistricts.reduce((s, d) => s + d.ras3, 0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer style={{ justifyContent: 'center', padding: 'clamp(1rem, 2vw, 2rem)', flexWrap: 'wrap' }}>
                <Button variant="success" size="lg" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.5rem)', minWidth: 120 }} onClick={() => setShowModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </Container>
      </section>
    </>
  );
};

export default RasActivityAdminPage;
